import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AppContentTypesEnum} from '../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {SettingsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {computeAccountsSetupGuideCompletedTasks} from '../../../helpers/accountHelpers';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {AccountsSetupGuideTasksEnum} from '../../../models/api/accountsSetupGuideModel';
import {pushContentTypeAction} from '../../../store/panels/app/appPanelRouteActions';
import {SetupGuide} from './common/setupGuide';
import {AddAccountCustomFieldTaskContainer} from './tasks/addAccountCustomFieldTaskContainer';
import {AddAccountTaskContainer} from './tasks/addAccountTaskContainer';
import {DiscoverFrontAccountsTask} from './tasks/discoverFrontAccountsTask';

/*
 * Constants.
 */

const helpCenterUrl = 'https://help.front.com/t/60h1a1c/accounts-quickstart-guide';

/*
 * Props.
 */

interface AccountsSetupGuideProps {
  shouldHideCompletedGuide?: boolean;
  accountsSetupGuideCompletedTasks: ReadonlyArray<AccountsSetupGuideTasksEnum>;
  fetchAccountsSetupGuideCompletedTasksAsync: () => Promise<void>;
  onImportCSVClick?: () => void;
  onImportSalesforceClick?: () => void;
  // Dismissed state (from teammate preference).
  hasDismissedAccountsSetupGuide: boolean;
  updateHasDismissedAccountsSetupGuide: (arg: boolean) => void;
  settingsPanelId?: SettingsPanelId;
}

/*
 * Style.
 */

const StyledManageAccountsButtonDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

/*
 * Component.
 */

/**
 * This intermediary component computes the overall guide progress and passes the
 * isComplete status to each task. The task components are themselves responsible
 * for setting up their own label and action.
 */
export const AccountsSetupGuide: FC<AccountsSetupGuideProps> = ({
  shouldHideCompletedGuide,
  accountsSetupGuideCompletedTasks,
  fetchAccountsSetupGuideCompletedTasksAsync,
  onImportCSVClick,
  onImportSalesforceClick,
  hasDismissedAccountsSetupGuide,
  updateHasDismissedAccountsSetupGuide,
  settingsPanelId,
}) => {
  useInitialEffect(() => {
    fetchAccountsSetupGuideCompletedTasksAsync();
  });

  const dispatch = useAppDispatch();
  const onManageAccountsClick = () => {
    if (settingsPanelId) {
      dispatch(pushContentTypeAction(settingsPanelId, AppContentTypesEnum.CONTACTS));
    }
  };

  if (hasDismissedAccountsSetupGuide) {
    return null;
  }

  const {isDiscoverFrontAccountsComplete, isAddAccountCustomFieldComplete, isAddAccountComplete} =
    computeAccountsSetupGuideCompletedTasks(accountsSetupGuideCompletedTasks);

  // List of the isComplete status for each task.
  const taskIsCompletes = [
    isDiscoverFrontAccountsComplete,
    isAddAccountCustomFieldComplete,
    isAddAccountComplete,
  ];

  const numCompletedTasks = taskIsCompletes.filter(Boolean).length;
  const progress = Math.floor((numCompletedTasks / taskIsCompletes.length) * 100);

  if (progress >= 100 && shouldHideCompletedGuide) {
    return null;
  }

  const onDismiss = () => updateHasDismissedAccountsSetupGuide(true);

  return (
    <SetupGuide
      title={<Trans id="accounts-setup-guide-title">Set up accounts</Trans>}
      iconName="briefcaseOutline"
      settingsIconName="briefcaseFilled"
      progress={progress}
      onDismiss={onDismiss}
      helpCenterUrl={helpCenterUrl}
      renderFooter={() => renderManageAccountsButton(onManageAccountsClick)}
      settingsPanelId={settingsPanelId}
    >
      <DiscoverFrontAccountsTask isComplete={isDiscoverFrontAccountsComplete} />
      <AddAccountCustomFieldTaskContainer isComplete={isAddAccountCustomFieldComplete} />
      <AddAccountTaskContainer
        isComplete={isAddAccountComplete}
        onImportCSVClick={onImportCSVClick}
        onImportSalesforceClick={onImportSalesforceClick}
      />
    </SetupGuide>
  );
};

function renderManageAccountsButton(onManageAccountsClick: () => void) {
  return (
    <StyledManageAccountsButtonDiv>
      <Button variant={ButtonVariantsEnum.PRIMARY} isRounded onClick={onManageAccountsClick}>
        <Trans id="accounts-setup-guide-manage">Manage accounts</Trans>
      </Button>
    </StyledManageAccountsButtonDiv>
  );
}
